import { type Column, type RowData, Subscribe } from '@tanstack/react-table';
import type React from 'react';
import type { kkTableFeatures, TableInstance } from '../tableFeatures';
import { ColumnControls } from './ColumnControls';
import {
  type ColumnSortDirection,
  supportsSelectStyleFiltering,
} from './columnHeaderControlsShared';
import { SortButton } from './SortButton';

interface TableHeaderControlsProps<TData extends RowData> {
  label: React.ReactNode;
  data: TData[];
  table: TableInstance<TData>;
  column: Column<typeof kkTableFeatures, TData, unknown>;
  isFilterable?: boolean;
  isSortable?: boolean;
  sortDirection: ColumnSortDirection;
}

export function TableHeaderControls<TData extends RowData>({
  label,
  data,
  table,
  column,
  isFilterable,
  isSortable,
  sortDirection,
}: TableHeaderControlsProps<TData>) {
  const hasSupportedFilterControl =
    isFilterable && supportsSelectStyleFiltering(column.columnDef);

  if (hasSupportedFilterControl) {
    return (
      <Subscribe
        source={table.store}
        selector={(state) =>
          state.columnFilters.find(({ id }) => id === column.id)?.value
        }
      >
        {(filterValue) => (
          <ColumnControls<TData>
            data={data}
            table={table}
            column={column}
            filterValue={filterValue}
            label={label}
            isSortable={isSortable}
            sortDirection={sortDirection}
          />
        )}
      </Subscribe>
    );
  }

  return (
    <>
      <div className="text-nowrap self-center">{label}</div>
      <SortButton
        columnId={column.id}
        isSortable={isSortable}
        onClick={column.getToggleSortingHandler()}
        getNextSortingOrder={() => column.getNextSortingOrder()}
        sortDirection={sortDirection}
      />
    </>
  );
}
